/* La nómina, agrupada por sector.
 *
 * ── DE DÓNDE SALE ESTO ───────────────────────────────────────────────────────
 *
 * Nico, P-147: «la nómina de empleados no tiene a los administrativos ni de
 * taller». Los tiene: son 292 filas y 171 choferes, así que 121 son otra cosa.
 * Lo que faltaba no era el dato sino poder SEPARARLO — la pantalla ofrecía
 * Activos / Choferes / Bajas / Todos, y un administrativo sólo aparecía dentro
 * de «todos», mezclado entre 292.
 *
 * ── EL SECTOR SE DEDUCE, NO SE GUARDA ────────────────────────────────────────
 *
 * La nómina la importa Erick de su archivo y ahí no hay columna de sector: hay
 * `categoria` —el encuadre del convenio— y `calificacion` —lo que la persona
 * hace—, las dos texto libre. Agregar una columna propia significaría que
 * alguien la mantenga a mano y que se desincronice del archivo que manda.
 *
 * Así que se deduce de la categoría. Y por eso hay un sector «Otros» QUE SE
 * MUESTRA: el día que aparezca una categoría nueva, sus empleados van a estar
 * ahí a la vista con su nombre, en vez de repartidos en el sector que más se
 * le parezca. Una clasificación que adivina es peor que una que se declara
 * incompleta.
 *
 * ── Y LAS CATEGORÍAS VIENEN COMO VIENEN ──────────────────────────────────────
 *
 * Del archivo real: «Administartivo de 4 ta.», con el typo. Por eso se busca
 * por raíz («administ») y no por el nombre completo: la lista la escribe otro
 * sistema y no la vamos a corregir desde acá.
 */

export const SECTORES = [
  { id: "choferes", label: "Choferes" },
  { id: "taller", label: "Taller" },
  { id: "administracion", label: "Administración" },
  { id: "deposito", label: "Depósito" },
  { id: "jefaturas", label: "Jefaturas" },
  { id: "otros", label: "Otros" },
];

const sinAcentos = (t) =>
  String(t ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/* Raíces, no nombres completos. Ordenadas: la primera que aplica gana, así que
 * lo más específico va arriba. */
const PISTAS = [
  ["jefaturas", ["gerente", "jefe", "responsable de area", "enc."]],
  ["taller", ["mecanic", "mecánic", "oficial", "lavador", "chapista", "electricista"]],
  ["administracion", ["administ", "contable", "liquidac"]],
  ["deposito", ["peon", "recibidor", "autoelevador", "playa", "furgoner", "operativo", "deposit"]],
  ["choferes", ["chofer"]],
];

const buscarPista = (texto) => {
  const t = sinAcentos(texto);
  if (!t.trim()) return null;
  for (const [sector, raices] of PISTAS) {
    if (raices.some((r) => t.includes(sinAcentos(r)))) return sector;
  }
  return null;
};

/* El sector de una fila de la nómina, en cascada de tres.
 *
 * ── POR QUÉ TRES Y NO UNA ────────────────────────────────────────────────────
 *
 * 1 · `es_chofer` MANDA. Es la marca que ya usa el resto del hub para saber
 *     quién maneja. Que dos pantallas cuenten distinto los choferes sería peor
 *     que cualquier error de clasificación.
 *
 * 2 · Después la CALIFICACIÓN, que es lo que la persona hace.
 *
 * 3 · Y recién al final la CATEGORÍA, que es el encuadre del convenio.
 *
 * El orden importa y lo probó un caso real: PATIÑO tiene categoría «Chofer de
 * 1ra.» y calificación «Empleado Administrativo». Es administrativo; la
 * categoría es su encuadre gremial, no su tarea. La primera versión de esto
 * miraba la categoría primero y lo mandaba a Choferes — lo agarró un test que
 * ya existía por el mismo motivo.
 *
 * Pero la calificación tampoco alcanza sola: 86 de las 292 personas dicen
 * «Empleado» a secas, que no clasifica nada. Por eso hay una tercera pasada y
 * no dos. Ninguna de las dos columnas sirve por su cuenta.
 */
export function sectorDe(fila) {
  if (fila?.es_chofer) return "choferes";
  // La calificación específica. «Empleado» a secas no coincide con ninguna
  // raíz y por eso cae sola a la pasada siguiente.
  return buscarPista(fila?.calificacion) || buscarPista(fila?.categoria) || "otros";
}

/* Cuántos hay en cada sector, contando SÓLO a los que trabajan.
 *
 * Una baja de hace tres años no es «un administrativo»: mezclarla infla el
 * número que alguien va a usar para decidir. Las bajas tienen su propio
 * filtro. */
export function porSector(filas, { soloActivos = true } = {}) {
  const cuenta = Object.fromEntries(SECTORES.map((s) => [s.id, 0]));
  for (const f of filas || []) {
    if (soloActivos && !f?.trabaja) continue;
    cuenta[sectorDe(f)] += 1;
  }
  return cuenta;
}

/* Las categorías que cayeron en «Otros», para poder nombrarlas. Sin esto el
 * chip diría «Otros 4» y nadie sabría de qué cuatro habla. */
export function queHayEnOtros(filas) {
  const nombres = new Set();
  for (const f of filas || []) {
    if (sectorDe(f) !== "otros") continue;
    const como =
      [String(f?.calificacion || "").trim(), String(f?.categoria || "").trim()]
        .filter(Boolean)
        .join(" · ");
    nombres.add(como || "(sin categoría)");
  }
  return [...nombres].sort((a, b) => a.localeCompare(b, "es"));
}
